import React, { useMemo, useState } from "react";
import LoadingState from "../../Components/LoadingState";
import { InfoIcon } from "./icons";

// The timeseries endpoint splits each day by channel, so the chart shows where
// conversations came from rather than one undifferentiated total.
//
// Which channels exist is the response's to say - it has already dropped one -
// so this only supplies the presentation for the ones we recognise.
const SOURCE_META = {
  widget: { label: "Widget", color: "#3b82f6" },
  preview: { label: "Preview", color: "#f97316" },
  training: { label: "Training", color: "#22c55e" },
};

// For a channel the backend adds later: it still gets a colour and a legend
// entry rather than being dropped on the floor.
const EXTRA_COLORS = ["#8b5cf6", "#ec4899", "#14b8a6", "#eab308"];

const humanize = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

// Known channels keep their defined order; anything unfamiliar follows, so
// the legend does not reshuffle as daily counts change.
const sourcesIn = (series) => {
  const seen = new Set();
  series.forEach((day) =>
    Object.keys(day.bySource || {}).forEach((key) => seen.add(key)),
  );

  const known = Object.keys(SOURCE_META).filter((key) => seen.has(key));
  const extra = [...seen].filter((key) => !SOURCE_META[key]).sort();

  return [...known, ...extra].map((key, i) => ({
    key,
    label: SOURCE_META[key]?.label || humanize(key),
    color: SOURCE_META[key]?.color || EXTRA_COLORS[i % EXTRA_COLORS.length],
    get: (day) => day.bySource?.[key] || 0,
  }));
};

// The response has carried a per-channel split, and has also arrived without
// one. When there is no split the day totals are still worth plotting, so the
// chart falls back to a single series rather than drawing an empty grid.
const TOTAL_SERIES = [
  {
    key: "value",
    label: "Conversations",
    color: "#3b82f6",
    get: (day) => day.value || 0,
  },
  {
    key: "leads",
    label: "Leads",
    color: "#22c55e",
    get: (day) => day.leads || 0,
  },
];

const VB_WIDTH = 760;
const VB_HEIGHT = 300;
const PAD_LEFT = 36;
const PAD_RIGHT = 10;
const PAD_TOP = 20;
const PAD_BOTTOM = 28;
const PLOT_WIDTH = VB_WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_HEIGHT = VB_HEIGHT - PAD_TOP - PAD_BOTTOM;

// A round number above the tallest bar, so the axis doesn't end on 7.
const niceMax = (value) => {
  if (value <= 4) return 4;

  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
};

export default function ConversationsChart({ series = [], loading = false }) {
  const [mode, setMode] = useState("grouped");
  const [showTable, setShowTable] = useState(false);
  const [hovered, setHovered] = useState(null);

  const stacked = mode === "stacked";

  const sources = useMemo(() => {
    const split = sourcesIn(series);
    return split.length ? split : TOTAL_SERIES;
  }, [series]);

  const splitBySource = sources !== TOTAL_SERIES;
  // Anything with more than one series needs a legend to tell them apart,
  // and a layout to compare them in.
  const multiSeries = sources.length > 1;

  const { yMax, yTicks, peak } = useMemo(() => {
    // Grouped bars are measured against the tallest single channel; stacked
    // against the tallest day total.
    const tallest = series.reduce((max, day) => {
      const perSource = Math.max(...sources.map((s) => s.get(day)), 0);
      return Math.max(max, stacked ? day.value : perSource);
    }, 0);

    const max = niceMax(tallest);

    return {
      yMax: max,
      yTicks: [0, 1, 2, 3, 4].map((i) => (max / 4) * i),
      peak: series.reduce(
        (best, day) => (day.value > (best?.value ?? -1) ? day : best),
        null,
      ),
    };
  }, [series, stacked, sources]);

  const bandWidth = series.length ? PLOT_WIDTH / series.length : PLOT_WIDTH;
  // A little air between days; the rest of the band belongs to the bars.
  const innerWidth = bandWidth * 0.72;
  const barWidth = stacked
    ? innerWidth
    : innerWidth / Math.max(1, sources.length);

  const bandX = (i) => PAD_LEFT + i * bandWidth;
  const barsX = (i) => bandX(i) + (bandWidth - innerWidth) / 2;
  const yAt = (v) => PAD_TOP + PLOT_HEIGHT - (v / yMax) * PLOT_HEIGHT;
  const heightOf = (v) => (v / yMax) * PLOT_HEIGHT;

  // Every label would collide on a 30-day window, so only every nth is drawn.
  const labelStep = Math.max(1, Math.ceil(series.length / 14));

  // Column sums, so the table answers "how many altogether" on its own.
  const totals = useMemo(
    () =>
      series.reduce(
        (sum, day) => {
          sources.forEach((source) => {
            sum[source.key] = (sum[source.key] || 0) + source.get(day);
          });
          sum.value += day.value || 0;
          sum.leads += day.leads || 0;
          return sum;
        },
        { value: 0, leads: 0 },
      ),
    [series, sources],
  );

  const active = hovered !== null ? series[hovered] : null;

  const renderPlot = () => {
    if (loading) {
      return (
        <div className="dash-chart-wrap dash-chart-wrap--state">
          <LoadingState title="Loading chart" inline />
        </div>
      );
    }

    if (!series.length) {
      return (
        <div className="dash-chart-wrap dash-chart-wrap--state">
          <p className="dash-empty">No conversations in this period yet.</p>
        </div>
      );
    }

    return (
      <div className="dash-chart-wrap">
        <svg
          className="dash-chart-svg"
          viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Conversations per day by source, bar chart"
          onMouseLeave={() => setHovered(null)}
        >
          {yTicks.map((tick) => (
            <line
              key={tick}
              x1={PAD_LEFT}
              x2={VB_WIDTH - PAD_RIGHT}
              y1={yAt(tick)}
              y2={yAt(tick)}
              className="dash-chart-gridline"
            />
          ))}

          {yTicks.map((tick) => (
            <text
              key={tick}
              x={PAD_LEFT - 10}
              y={yAt(tick) + 4}
              className="dash-chart-ytick"
              textAnchor="end"
            >
              {Math.round(tick)}
            </text>
          ))}

          {series.map((day, i) => {
            // One transparent band per day catches the pointer, so hovering
            // works in the gaps between bars and on a day with no bars at all.
            let stackTop = PAD_TOP + PLOT_HEIGHT;

            return (
              <g key={day.date || i}>
                <rect
                  x={bandX(i)}
                  y={PAD_TOP}
                  width={bandWidth}
                  height={PLOT_HEIGHT}
                  className={`an-bar-band${hovered === i ? " is-active" : ""}`}
                  onMouseEnter={() => setHovered(i)}
                />

                {sources.map((source, s) => {
                  const value = source.get(day);
                  if (!value) return null;

                  const height = heightOf(value);
                  const x = stacked ? barsX(i) : barsX(i) + s * barWidth;
                  const y = stacked ? stackTop - height : yAt(value);

                  if (stacked) stackTop -= height;

                  return (
                    <rect
                      key={source.key}
                      x={x}
                      y={y}
                      width={Math.max(1, barWidth - (stacked ? 0 : 1.5))}
                      height={Math.max(1, height)}
                      fill={source.color}
                      className="an-bar"
                      onMouseEnter={() => setHovered(i)}
                    />
                  );
                })}
              </g>
            );
          })}

          {series.map((day, i) =>
            i % labelStep === 0 ? (
              <text
                key={`x-${day.date || i}`}
                x={bandX(i) + bandWidth / 2}
                y={VB_HEIGHT - 6}
                className="dash-chart-xtick"
                textAnchor="middle"
              >
                {day.label}
              </text>
            ) : null,
          )}
        </svg>

        {active && (
          <div
            className="dash-chart-tooltip an-bar-tooltip"
            style={{
              left: `${((bandX(hovered) + bandWidth / 2) / VB_WIDTH) * 100}%`,
            }}
          >
            <span className="dash-chart-tooltip-date">{active.label}</span>

            {sources.map((source) => (
              <span className="an-bar-tooltip-row" key={source.key}>
                <span
                  className="an-bar-tooltip-dot"
                  style={{ background: source.color }}
                />
                {source.label}
                <strong>{source.get(active)}</strong>
              </span>
            ))}

            {splitBySource && (
              <>
                <span className="an-bar-tooltip-row is-total">
                  Total
                  <strong>{active.value}</strong>
                </span>
                <span className="an-bar-tooltip-row">
                  Leads
                  <strong>{active.leads || 0}</strong>
                </span>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="dash-card dash-chart-card an-chart-card">
      <div className="dash-card-header an-bar-header">
        <div>
          <h2 className="dash-card-title">
            {splitBySource ? "Conversations by source" : "Conversations"}
            <span
              className="an-info-icon"
              title="Conversations each day, split by where they came from."
            >
              <InfoIcon />
            </span>
          </h2>
          {peak && (
            <p className="an-bar-peak">
              {peak.value} conversation{peak.value === 1 ? "" : "s"} ·{" "}
              {peak.leads || 0} lead
              {peak.leads === 1 ? "" : "s"}
            </p>
          )}
        </div>

        <div className="an-bar-controls">
          <button
            type="button"
            className="an-bar-table-btn"
            onClick={() => setShowTable((open) => !open)}
            aria-expanded={showTable}
          >
            {showTable ? "Hide data table" : "Show data table"}
          </button>
        </div>
      </div>

      {multiSeries && (
        <div className="an-legend">
          {sources.map((source) => (
            <span className="an-legend-item" key={source.key}>
              <span
                className="an-legend-swatch"
                style={{ background: source.color }}
              />
              {source.label}
            </span>
          ))}
        </div>
      )}

      {renderPlot()}

      {/* The same numbers in a form that can be read and copied, which a
          chart alone can't offer. */}
      {showTable && series.length > 0 && (
        <div className="an-dt">
          <table>
            <thead>
              <tr>
                <th scope="col">Day</th>
                {sources.map((source) => (
                  <th
                    scope="col"
                    className="an-dt-num text-center"
                    key={source.key}
                  >
                    <span className="an-dt-head">
                      <span
                        className="an-dt-dot"
                        style={{ background: source.color }}
                      />
                      {source.label}
                    </span>
                  </th>
                ))}
                {splitBySource && (
                  <>
                    <th scope="col" className="an-dt-num text-center">
                      Total
                    </th>
                    <th scope="col" className="an-dt-num text-center">
                      Leads
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {series.map((day, i) => (
                <tr
                  key={day.date || i}
                  className={hovered === i ? "is-hovered" : undefined}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <th scope="row">{day.label}</th>
                  {sources.map((source) => (
                    <td className="an-dt-num text-center" key={source.key}>
                      {/* A zero is real data, but printing it in full ink
                          makes empty channels compete with busy ones. */}
                      <span className={source.get(day) ? "" : "is-zero"}>
                        {source.get(day)}
                      </span>
                    </td>
                  ))}
                  {splitBySource && (
                    <>
                      <td className="an-dt-num an-dt-total text-center">
                        {day.value}
                      </td>
                      <td className="an-dt-num text-center">
                        <span className={day.leads ? "" : "is-zero"}>
                          {day.leads || 0}
                        </span>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <th scope="row">{series.length} days</th>
                {sources.map((source) => (
                  <td className="an-dt-num text-center" key={source.key}>
                    {totals[source.key] || 0}
                  </td>
                ))}
                {splitBySource && (
                  <>
                    <td className="an-dt-num an-dt-total text-center">
                      {totals.value}
                    </td>
                    <td className="an-dt-num text-center">{totals.leads}</td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
